/**
 * Optimized HomeHowWeWork Component (Benefits Section)
 * Performance improvements:
 * - React.memo for preventing unnecessary re-renders
 * - useMemo for static config
 * - useCallback for event handlers
 * - Intersection Observer for view tracking
 * - Analytics tracking for views and link clicks
 * - Next.js Image component for optimized image loading
 */

'use client';

import { memo, useMemo, useCallback, useRef, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { benefitsConfig } from "@/constant/home";
import { useIntersectionObserver, useAnalytics } from '@/hooks/usePerformance';

export type Benefit = {
    icon: JSX.Element;
    title: string;
    featuresList: string[];
    description: JSX.Element;
    link: {
        href: string;
        text: string;
        srText?: string;
    };
};

// Memoized BenefitItem component
const BenefitItem = memo(function BenefitItem({
    icon,
    title,
    description,
    featuresList,
    link,
    index
}: Benefit & { index: number }) {
    const cardRef = useRef<HTMLDivElement>(null);
    const hasTrackedView = useRef(false);
    const { trackEvent } = useAnalytics();

    // Track card visibility
    const isInView = useIntersectionObserver(cardRef, {
        threshold: 0.3,
        rootMargin: '50px',
    });

    // Track when card becomes visible (only once)
    useEffect(() => {
        if (isInView && !hasTrackedView.current) {
            trackEvent('benefit_card_view', {
                benefit_title: title,
                benefit_index: index,
            });
            hasTrackedView.current = true;
        }
    }, [isInView, title, index, trackEvent]);

    // Track link clicks
    const handleLinkClick = useCallback(() => {
        trackEvent('benefit_link_clicked', {
            benefit_title: title,
            benefit_index: index,
            link_text: link.text,
            link_destination: link.href,
        });
    }, [title, index, link.text, link.href, trackEvent]);

    return (
        <div ref={cardRef} className="benefits2_list-item bg-white">
            <div>
                <div className="benefits2_icon-wrapper">
                    <div className="icon-1x1-medium w-embed">{icon}</div>
                </div>
                <div className="padding-top padding-xsmall"></div>
                <div className="text-weight-medium text-black">{title}</div>
                <div className="padding-top padding-tiny"></div>
                <div className="text-size-small text-color-gray-50 dark:text-gray-800">{description}</div>
                <div className="pricing-features_list mt-2">
                    <ul role="list" className="w-list-unstyled">
                        {featuresList.map((feature, idx) => (
                            <li key={`feature-${index}-${idx}`} className="text-xs text-color-gray-50 dark:text-gray-800">
                                {feature}
                            </li>
                        ))}
                    </ul>
                </div>
            </div>

            <Link
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                className="link w-inline-block"
                onClick={handleLinkClick}
                aria-label={link.srText || `${link.text} - Opens in new tab`}
            >
                <div className="link_content-wrapper">
                    <div className="link_text">{link.text}</div>
                    <div className="link_icon w-embed" aria-hidden="true">
                        <svg aria-hidden="true" role="img" xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12">
                            <g fill="currentColor">
                                <path
                                    d="M1.293,10.707a1,1,0,0,0,1.414,0L7.854,5.561l2.292,2.293A.5.5,0,0,0,10.5,8a.489.489,0,0,0,.191-.038A.5.5,0,0,0,11,7.5v-6a.5.5,0,0,0-.5-.5h-6a.5.5,0,0,0-.354.854L6.439,4.146,1.293,9.293A1,1,0,0,0,1.293,10.707Z"
                                    fill="currentColor"
                                ></path>
                            </g>
                        </svg>
                    </div>
                </div>
            </Link>
        </div>
    );
});

function HomeHowWeWorkComponent() {
    const sectionRef = useRef<HTMLDivElement>(null);
    const hasTrackedView = useRef(false);
    const { trackEvent } = useAnalytics();

    // Memoize config to prevent re-creation
    const config = useMemo(() => benefitsConfig, []);

    // Track section visibility
    const isInView = useIntersectionObserver(sectionRef, {
        threshold: 0.2,
        rootMargin: '50px',
    });

    // Track when section becomes visible (only once)
    useEffect(() => {
        if (isInView && !hasTrackedView.current) {
            trackEvent('benefits_section_view', {
                section_headline: config.headline,
                total_benefits: config.benfitsList.length,
            });
            hasTrackedView.current = true;
        }
    }, [isInView, config.headline, config.benfitsList.length, trackEvent]);

    return (
        <div ref={sectionRef} className="section_home-benefits2 bg-[var(--gray--10)] dark:bg-[var(--gray--100)]">
            <div className="padding-global">
                <div className="container-large">
                    <div className="padding-vertical padding-xhuge">
                        <div className="max-width-large">
                            <div className="padding-bottom padding-custom2"></div>
                            <h2 className="text-[var(--primary--burgundy)] dark:text-[var(--primary--pink)]">
                                {config.headline}
                            </h2>
                        </div>
                        <div className="padding-bottom padding-xlarge"></div>
                        <div className="benefits2_component">
                            <div className="benefits2_content-left">
                                <div className="benefits2_testimonial">
                                    <div className="benefits2_testimonial_content">
                                        <div className="padding-bottom padding-xsmall"></div>
                                    </div>
                                    <div className="benefits2_image-wrapper">
                                        {config.image && (
                                            <Image
                                                src={config.image}
                                                alt={config.subHeadline.replace(/<[^>]*>/g, '')} // Strip HTML for alt text
                                                width={1280}
                                                height={720}
                                                className="benefits2_image"
                                                loading="lazy"
                                                priority={false}
                                            />
                                        )}
                                    </div>
                                    <div className="benefits2_testimonial_overlay"></div>
                                </div>
                            </div>
                            <div className="benefits2_content-right">
                                <p className="text-size-medium" dangerouslySetInnerHTML={{ __html: config.subHeadline }} />
                                <div className="padding-bottom padding-large"></div>
                                <div className="benefits2_list-wrapper">
                                    {config.benfitsList.map((benefit, idx) => (
                                        <BenefitItem key={`benefit-${idx}`} {...benefit} index={idx} />
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

// Export memoized component
export const HomeHowWeWork = memo(HomeHowWeWorkComponent);
HomeHowWeWork.displayName = 'HomeHowWeWork';

// Default export for backward compatibility
export default HomeHowWeWork;
